import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const MedicalRecordsModal = ({ isOpen, onClose, clinicId }) => {
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPet, setSelectedPet] = useState('all');
  const [pets, setPets] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);

  useEffect(() => {
    if (isOpen && clinicId) {
      fetchMedicalRecords();
      fetchPets();
    }
  }, [isOpen, clinicId]);

  useEffect(() => {
    if (selectedPet === 'all') {
      setFilteredRecords(medicalRecords);
    } else {
      setFilteredRecords(medicalRecords.filter(record => record.pet_id === selectedPet));
    }
  }, [selectedPet, medicalRecords]);

  const fetchMedicalRecords = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('medical_records')
        .select(`
          *,
          pets!inner(name, species, breed, owners!inner(first_name, last_name, clinic_id))
        `)
        .eq('pets.owners.clinic_id', clinicId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMedicalRecords(data || []);
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching medical records:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPets = async () => {
    try {
      const { data, error } = await supabase
        .from('pets')
        .select(`
          id,
          name,
          species,
          breed,
          owners!inner(first_name, last_name)
        `)
        .eq('owners.clinic_id', clinicId)
        .is('deleted_at', null);

      if (error) throw error;
      setPets(data || []);
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching pets:', error);
    }
  };

  const generatePDF = async (record = null) => {
    const recordsToGenerate = record ? [record] : filteredRecords;
    
    if (recordsToGenerate.length === 0) return;

    const pdf = new jsPDF('p', 'mm', 'a4');
    let yPosition = 20;
    const pageHeight = 280;
    const margin = 20;
    const lineHeight = 8;

    recordsToGenerate.forEach((record, index) => {
      if (yPosition > pageHeight) {
        pdf.addPage();
        yPosition = 20;
      }

      // Header
      pdf.setFontSize(18);
      pdf.setTextColor(16, 185, 129); // Green color
      pdf.text('TailClinic - Ιατρικό Αρχείο', margin, yPosition);
      yPosition += 15;

      // Pet Information
      pdf.setFontSize(14);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Κατοικίδιο: ${record.pets?.name || 'N/A'}`, margin, yPosition);
      yPosition += lineHeight;
      pdf.text(`Είδος: ${record.pets?.species || 'N/A'}`, margin, yPosition);
      yPosition += lineHeight;
      pdf.text(`Ράτσα: ${record.pets?.breed || 'N/A'}`, margin, yPosition);
      yPosition += lineHeight;
      pdf.text(`Ιδιοκτήτης: ${record.pets?.owners?.first_name || 'N/A'} ${record.pets?.owners?.last_name || 'N/A'}`, margin, yPosition);
      yPosition += lineHeight;

      // Record Information
      yPosition += 5;
      pdf.setFontSize(12);
      pdf.text(`Ημερομηνία: ${new Date(record.created_at).toLocaleDateString('el-GR')}`, margin, yPosition);
      yPosition += lineHeight;
      pdf.text(`Κτηνίατρος: Δρ. ${record.vets?.first_name || 'N/A'} ${record.vets?.last_name || 'N/A'}`, margin, yPosition);
      yPosition += lineHeight;

      // Diagnosis
      if (record.diagnosis) {
        yPosition += 5;
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Διάγνωση:', margin, yPosition);
        yPosition += lineHeight;
        pdf.setFont(undefined, 'normal');
        const diagnosisLines = pdf.splitTextToSize(record.diagnosis, 170);
        diagnosisLines.forEach(line => {
          if (yPosition > pageHeight) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(line, margin, yPosition);
          yPosition += lineHeight;
        });
      }

      // Treatment
      if (record.treatment) {
        yPosition += 5;
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Θεραπεία:', margin, yPosition);
        yPosition += lineHeight;
        pdf.setFont(undefined, 'normal');
        const treatmentLines = pdf.splitTextToSize(record.treatment, 170);
        treatmentLines.forEach(line => {
          if (yPosition > pageHeight) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(line, margin, yPosition);
          yPosition += lineHeight;
        });
      }

      // Notes
      if (record.notes) {
        yPosition += 5;
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Σημειώσεις:', margin, yPosition);
        yPosition += lineHeight;
        pdf.setFont(undefined, 'normal');
        const notesLines = pdf.splitTextToSize(record.notes, 170);
        notesLines.forEach(line => {
          if (yPosition > pageHeight) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(line, margin, yPosition);
          yPosition += lineHeight;
        });
      }

      // Add separator between records
      if (index < recordsToGenerate.length - 1) {
        yPosition += 10;
        pdf.line(margin, yPosition, margin + 170, yPosition);
        yPosition += 10;
      }
    });

    // Save PDF
    const filename = record 
      ? `medical_record_${record.pets?.name || 'pet'}_${new Date().toISOString().split('T')[0]}.pdf`
      : `medical_records_${new Date().toISOString().split('T')[0]}.pdf`;
    
    pdf.save(filename);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('el-GR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gray-800 text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Ιατρικά Αρχεία</h2>
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-white text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Filters */}
          <div className="mb-6 flex items-center space-x-4">
            <label className="text-gray-700 font-medium">Φιλτράρισμα ανά κατοικίδιο:</label>
            <select
              value={selectedPet}
              onChange={(e) => setSelectedPet(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">Όλα τα κατοικίδια</option>
              {pets.map((pet) => (
                <option key={pet.id} value={pet.id}>
                  {pet.name} ({pet.species})
                </option>
              ))}
            </select>

            <button
              onClick={() => generatePDF()}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              📄 Εξαγωγή PDF Όλων
            </button>
          </div>

          {/* Records Table */}
          {loading ? (
            <div className="text-center py-8">
              <div className="text-gray-500">Φόρτωση ιατρικών αρχείων...</div>
            </div>
          ) : filteredRecords.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-4 py-2 text-left">Ημερομηνία</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Κατοικίδιο</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Ιδιοκτήτης</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Κτηνίατρος</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Διάγνωση</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Θεραπεία</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Ενέργειες</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2">
                        {formatDate(record.created_at)}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <div>
                          <div className="font-medium">{record.pets?.name}</div>
                          <div className="text-sm text-gray-600">
                            {record.pets?.species} - {record.pets?.breed}
                          </div>
                        </div>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        {record.pets?.owners?.first_name} {record.pets?.owners?.last_name}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        Δρ. {record.vets?.first_name} {record.vets?.last_name}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 max-w-xs">
                        <div className="truncate" title={record.diagnosis}>
                          {record.diagnosis || '-'}
                        </div>
                      </td>
                      <td className="border border-gray-300 px-4 py-2 max-w-xs">
                        <div className="truncate" title={record.treatment}>
                          {record.treatment || '-'}
                        </div>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <button
                          onClick={() => generatePDF(record)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                          title="Εξαγωγή PDF"
                        >
                          📄 PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Δεν βρέθηκαν ιατρικά αρχεία για την επιλεγμένη φίλτρο.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicalRecordsModal; 